import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import Card from '../../components/ui/Card'
import { formatCurrency } from '../../lib/format'

const COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
]

function CategoryPieChart({ transactions, type = 'expense', title }) {
  // Group by category
  const byCategory = transactions
    .filter(t => t.type === type)
    .reduce((acc, t) => {
      const category = t.category || 'Uncategorized'
      if (!acc[category]) {
        acc[category] = 0
      }
      acc[category] += parseFloat(t.amount)
      return acc
    }, {})

  const data = Object.entries(byCategory)
    .map(([name, value], index) => ({
      name,
      value,
      color: COLORS[index % COLORS.length],
    }))
    .sort((a, b) => b.value - a.value)

  const totalAmount = data.reduce((sum, c) => sum + c.value, 0)
  const chartTitle = title || (type === 'expense' ? 'Expenses by Category' : 'Revenue by Category')

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const item = payload[0]
      const percentage = ((item.value / totalAmount) * 100).toFixed(1)
      return (
        <div style={{
          background: 'white',
          padding: '8px 12px',
          border: '1px solid var(--gray-200)',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, fontWeight: 500 }}>{item.name}</p>
          <p style={{ margin: 0, color: 'var(--gray-600)' }}>
            {formatCurrency(item.value)} ({percentage}%)
          </p>
        </div>
      )
    }
    return null
  }

  return (
    <Card>
      <h3 className="card-title mb-4">{chartTitle}</h3>

      {data.length === 0 ? (
        <p className="text-muted text-center py-8">No data to display</p>
      ) : (
        <>
          <div style={{ width: '100%', height: 250 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Center total */}
          <div className="text-center mb-4" style={{ marginTop: '-140px', position: 'relative', zIndex: 1 }}>
            <p className="text-sm text-muted mb-0">Total</p>
            <p className="text-xl font-bold" style={{ margin: 0 }}>{formatCurrency(totalAmount)}</p>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-2 mt-16">
            {data.slice(0, 6).map(item => {
              const percentage = ((item.value / totalAmount) * 100).toFixed(1)
              return (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        background: item.color,
                      }}
                    />
                    <span className="text-sm">{item.name}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted mr-2">{percentage}%</span>
                    <span className="font-medium">{formatCurrency(item.value)}</span>
                  </div>
                </div>
              )
            })}
            {data.length > 6 && (
              <p className="text-sm text-muted text-center mt-2">
                +{data.length - 6} more categories
              </p>
            )}
          </div>
        </>
      )}
    </Card>
  )
}

export default CategoryPieChart
